import React, { useState, useEffect } from "react";
import "./LatestHappenings.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const eventConfigs = [
  { endpoint: "/vista-with-images", tag: "STUDENT ACTIVITY", title: "Student Activity", placeholder: "/Events/Vista3-scaled.jpg" },
  { endpoint: "/ecell-with-images", tag: "E-CELL", title: "E-Cell", placeholder: "/Events/ecell.jpg" },
  { endpoint: "/codingclub-with-images", tag: "CODING CLUB", title: "Coding Club", placeholder: "/Events/TIRA-ROBOT.jpeg" },
  { endpoint: "/sportsday-with-images", tag: "SPORTS", title: "Sports Day", placeholder: "/Events/Sports-Day-2023.jpg" },
  { endpoint: "/trekking-with-images", tag: "TREKKING", title: "Trekking", placeholder: "/Events/Trek2.jpg" },
  { endpoint: "/freshersfarewell-with-images", tag: "FRESHERS & FAREWELL", title: "Freshers & Farewell", placeholder: "/Events/farewell.jpg" },
];

const fallbackEvents = eventConfigs.map((config) => ({
  title: config.title,
  tag: config.tag,
  images: [],
}));

const getImageSrc = (img) => {
  if (!img) return null;
  if (typeof img === "string" && (img.startsWith("http") || img.startsWith("data:"))) return img;
  return `data:image/jpeg;base64,${img}`;
};

const sanitizeImages = (activity) => {
  const images = [
    activity?.feature_image,
    activity?.image1,
    activity?.image2,
    activity?.image3,
    activity?.image4,
  ].filter(Boolean);

  return [...new Set(images)].map((img) => getImageSrc(img));
};

export const getLatestActivityFromPayload = (data) => {
  const activities = Object.values(data || {}).flat();

  if (!activities.length) return null;

  return activities.reduce((latest, activity) => {
    const currentDate = activity?.selected_date ? new Date(activity.selected_date) : null;
    const latestDate = latest?.selected_date ? new Date(latest.selected_date) : null;

    if (!currentDate || Number.isNaN(currentDate.getTime())) return latest;
    if (!latestDate || Number.isNaN(latestDate.getTime())) return activity;

    return currentDate > latestDate ? activity : latest;
  }, null);
};

const EventCard = ({ event }) => {
  const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const [showPlaceholder, setShowPlaceholder] = useState(true);

  const nextSlide = () => {
    setCurrentImgIndex((prev) => (prev === event.images.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentImgIndex((prev) => (prev === 0 ? event.images.length - 1 : prev - 1));
  };

  useEffect(() => {
    setShowPlaceholder(true);
    setCurrentImgIndex(0);

    if (!event.images.length || event.images.length < 2) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setCurrentImgIndex((prev) => (prev === event.images.length - 1 ? 0 : prev + 1));
    }, 2500);

    return () => clearInterval(intervalId);
  }, [event.images]);

  useEffect(() => {
    if (event.images.length > 0) {
      const timer = window.setTimeout(() => setShowPlaceholder(false), 50);
      return () => window.clearTimeout(timer);
    }

    return undefined;
  }, [event.images.length]);

  return (
    <div className="lh-event-card">
      <div className="lh-card-tab">
        <span className="lh-tab-text">{event.tag}</span>
      </div>

      <div className="lh-card-body">
        <div className="lh-image-wrapper">
          <img
            src={event.placeholder || "/Events/image00038.jpeg"}
            alt="Loading placeholder"
            className={`lh-event-image ${showPlaceholder ? "active" : ""}`}
          />

          {event.images.length > 0 ? (
            event.images.map((imgSrc, i) => (
              <img
                key={`${event.title}-${i}`}
                src={imgSrc}
                alt={`${event.title} - ${i + 1}`}
                className={`lh-event-image ${i === currentImgIndex && !showPlaceholder ? "active" : ""}`}
              />
            ))
          ) : (
            <div className="lh-empty-state">No images available from the source yet.</div>
          )}

          {event.images.length > 1 && (
            <>
              <button className="lh-carousel-btn left" onClick={prevSlide} aria-label="Previous image">
                &#10094;
              </button>
              <button className="lh-carousel-btn right" onClick={nextSlide} aria-label="Next image">
                &#10095;
              </button>

              <div className="lh-carousel-dots">
                {event.images.map((_, i) => (
                  <span
                    key={`${event.title}-dot-${i}`}
                    className={`lh-dot ${i === currentImgIndex ? "active" : ""}`}
                    onClick={() => setCurrentImgIndex(i)}
                  ></span>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="lh-card-main">
          <div className="lh-event-title">{event.title}</div>
          <div className="lh-corner-tl"></div>
          <div className="lh-corner-br"></div>
        </div>
      </div>
    </div>
  );
};

const LatestHappenings = () => {
  const [events, setEvents] = useState(fallbackEvents);

  useEffect(() => {
    let isMounted = true;

    const loadLatestEvents = async () => {
      const resolvedEvents = await Promise.all(
        eventConfigs.map(async (config) => {
          try {
            const response = await fetch(`${API_URL}${config.endpoint}`);
            if (!response.ok) throw new Error(`Failed to load ${config.endpoint}`);

            const payload = await response.json();
            const latestActivity = getLatestActivityFromPayload(payload);

            if (!latestActivity) {
              return {
                title: config.title,
                tag: config.tag,
                images: fallbackEvents[eventConfigs.indexOf(config)]?.images || [],
                placeholder: config.placeholder,
              };
            }

            return {
              title: latestActivity.title || config.title,
              tag: config.tag,
              images: sanitizeImages(latestActivity),
            };
          } catch (error) {
            return {
              title: config.title,
              tag: config.tag,
              images: fallbackEvents[eventConfigs.indexOf(config)]?.images || [],
              placeholder: config.placeholder,
            };
          }
        })
      );

      if (isMounted) {
        setEvents(resolvedEvents);
      }
    };

    loadLatestEvents();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="lh-container">
      <div className="rs5865f2-titleWrap">
        <div className="rs5865f2-titleRow">
          <div className="rs5865f2-titlePlain">Our</div>
          <div className="rs5865f2-titleGradient">Events</div>
        </div>
        <div className="rs5865f2-gradientLine" />
      </div>

      <div className="lh-events-grid">
        {events.map((event, index) => (
          <EventCard key={`${event.title}-${index}`} event={event} />
        ))}
      </div>
    </div>
  );
};

export default LatestHappenings;